import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { IoLogoWhatsapp } from 'react-icons/io5';
import './OrderCss/Orders.css';
import OrderDetailsModal from '../../components/OrdensComponents/Details';
import { useNavigate } from 'react-router-dom';
import Conect from '../../services/api/Conect';
import OrderModal from './Components/OrderModal';
import NotificationsAlert from '../../services/NotificationsAlert';

// ─── Constantes fora do componente (não recriam a cada render) ───────────────

const getToken = () => localStorage.getItem('fptoken');

const INITIAL_ORDER = {
  id_cli: null,
  id_vehicle: null,
  cliente: '',
  projectName: '',
  phone: '',
  email: '',
  isWhatsapp: false,
  plate: '',
  brand: '',
  model: '',
  year: '',
  vehicleType: 'veiculo',
  vehicleDescription: '',
  serviceDescription: [
    {
      Service: '',
      valor: '',
      OBS: '',
      done: false
    }
  ],
};

const STATUS_MAP = {
  open: { label: 'Em aberto', cls: 'o-badge-open' },
  pending: { label: 'Aguardando', cls: 'o-badge-pending' },
  done: { label: 'Concluída', cls: 'o-badge-done' },
};

// Parseador de services seguro e reutilizável
const parseServices = (raw) => {
  try { return JSON.parse(raw || '[]'); }
  catch { return []; }
};

// Formatador de total — fora do componente, sem dependências de estado
const calcTotal = (services = []) =>
  services
    .reduce((acc, s) => acc + (parseFloat(String(s.valor).replace(',', '.')) || 0), 0)
    .toLocaleString('pt-BR', { minimumFractionDigits: 2 });

// Helpers de API (não precisam de estado; recebem token por parâmetro)
const apiGet = async (url) => {
  try {
    const { data } = await Conect.get(`${url}${getToken()}`);
    return data.erro ? [] : (data.data ?? []);
  } catch (err) {
    console.error(`GET ${url}:`, err);
    return [];
  }
};

const apiPost = async (url, payload = {}) => {
  try {
    const { data } = await Conect.post(`${url}${getToken()}`, payload);
    return data.erro ? null : (data.data ?? null);
  } catch (err) {
    console.error(`POST ${url}:`, err);
    return null;
  }
};

// ─── Subcomponente estável ────────────────────────────────────────────────────

const StatusBadge = React.memo(({ status }) => {
  const { label, cls } = STATUS_MAP[status] ?? STATUS_MAP.open;
  return <span className={`o-status-badge ${cls}`}>{label}</span>;
});

// ─── Componente principal ─────────────────────────────────────────────────────

const Orders = () => {
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [clients, setClients] = useState([]);

  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [vehicleSuggestions, setVehicleSuggestions] = useState([]);

  const [newOrder, setNewOrder] = useState(INITIAL_ORDER);

  // ─── Helpers de state ───────────────────────────────────────────────────────

  const resetOrder = useCallback(() => setNewOrder(INITIAL_ORDER), []);
  const updateOrder = useCallback((data) =>
    setNewOrder(prev => ({ ...prev, ...data })), []);

  // ─── Fetches ────────────────────────────────────────────────────────────────

  const fetchVehicles = useCallback(async () => setVehicles(await apiGet('/listallvehicles')), []);
  const fetchCostumers = useCallback(async () => setClients(await apiGet('/listallcostumers')), []);
  const fetchBrands = useCallback(async () => setBrands(await apiGet('/listallbrands')), []);

  const fetchModels = useCallback(async (brandId) => {
    if (!brandId) { setModels([]); return; }
    const data = await apiPost('/listmodelwherebrand', { brand_id: Number(brandId) });
    setModels(data ?? []);
  }, []);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    const data = await apiGet('/listallorders');
    if (Array.isArray(data)) {
      setOrders(data.map(order => ({
        id: order.id,
        order_number: order.id,
        cliente: order.client_name,
        projectName: order.client_nick,
        cpf: order.client_cpf,
        phone: order.client_phone || '',
        whatsapp: order.client_phone_whatss,
        plate: order.plate,
        brand: order.brand_name,
        model: order.model_name,
        year: order.year,
        vehicleType: order.type,
        vehicleDescription: order.description,
        serviceDescription: parseServices(order.services),
        status: order.status,
        date: order.date,
      })));
    }
    setLoading(false);
  }, []);

  // Carga inicial em paralelo
  useEffect(() => {
    Promise.all([fetchOrders(), fetchVehicles(), fetchCostumers(), fetchBrands()]);
  }, [fetchOrders, fetchVehicles, fetchCostumers, fetchBrands]);

  // Modelos ao trocar brand no formulário
  useEffect(() => {
    if (newOrder.brand) fetchModels(newOrder.brand);
  }, [newOrder.brand, fetchModels]);

  // ─── Inputs ─────────────────────────────────────────────────────────────────

  const handleChange = useCallback(({ target: { name, value, type, checked } }) => {
    updateOrder({ [name]: type === 'checkbox' ? checked : value });
  }, [updateOrder]);

  // ─── Serviços ───────────────────────────────────────────────────────────────

  const handleServiceChange = useCallback((index, field, value) => {
    setNewOrder(prev => ({
      ...prev,
      serviceDescription: prev.serviceDescription.map((s, i) =>
        i === index ? { ...s, [field]: value } : s
      ),
    }));
  }, []);

  const handleAddService = useCallback(() => {
    setNewOrder(prev => ({
      ...prev,
      serviceDescription: [...prev.serviceDescription, {
        Service: '',
        valor: '',
        OBS: '',
        done: false
      }],
    }));
  }, []);

  const handleRemoveService = useCallback((index) => {
    setNewOrder(prev => ({
      ...prev,
      serviceDescription: prev.serviceDescription.filter((_, i) => i !== index),
    }));
  }, []);

  // ─── Clientes ───────────────────────────────────────────────────────────────

  // Set para lookup O(1) de clientes por nome/telefone
  const clientsRef = useRef(clients);
  useEffect(() => { clientsRef.current = clients; }, [clients]);

  const handleClientChange = useCallback((value) => {
    updateOrder({ cliente: value, id_cli: null });

    if (value.length < 2) { setClientSuggestions([]); return; }

    const q = value.toLowerCase();
    setClientSuggestions(
      clientsRef.current.filter(c =>
        c.name.toLowerCase().includes(q) || c.phone?.includes(value)
      )
    );
  }, [updateOrder]);

  const selectClient = useCallback((client) => {
    updateOrder({
      id_cli: client.id,
      cliente: client.name,
      phone: client.phone,
      email: client.email,
      projectName: client.nick_name || '',
    });
    setVehicleSuggestions(vehicles.filter(v => v.id_cli === client.id));
    setClientSuggestions([]);
  }, [updateOrder, vehicles]);

  // ─── Veículos ───────────────────────────────────────────────────────────────

  const vehiclesRef = useRef(vehicles);
  useEffect(() => { vehiclesRef.current = vehicles; }, [vehicles]);

  const handlePlateChange = useCallback((value) => {
    updateOrder({ plate: value, id_vehicle: null });

    if (value.length < 3) { setVehicleSuggestions([]); return; }

    const q = value.toUpperCase();
    setVehicleSuggestions(
      vehiclesRef.current.filter(v => v.plate?.toUpperCase().includes(q))
    );
  }, [updateOrder]);

  const selectVehicle = useCallback(async (vehicle) => {
    await fetchModels(vehicle.brand_id);
    updateOrder({
      id_vehicle: vehicle.id,
      plate: vehicle.plate,
      brand: vehicle.brand_id,
      model: vehicle.model_id,
      year: vehicle.year,
      vehicleType: vehicle.type,
      vehicleDescription: vehicle.description || '',
    });
    setVehicleSuggestions([]);
  }, [fetchModels, updateOrder]);

  // ─── Criar/encontrar cliente e veículo ──────────────────────────────────────

  const getOrCreateClient = useCallback(async () => {
    const found = await apiPost('/findclient', {
      name: newOrder.cliente, phone: newOrder.phone,
    });
    if (found?.id) return found.id;

    const created = await apiPost('/createcostumer', {
      name: newOrder.cliente,
      nick_name: newOrder.projectName,
      phone: newOrder.phone,
      phone_whatss: newOrder.isWhatsapp ? 1 : 0,
    });
    return created?.id;
  }, [newOrder]);

  const getOrCreateVehicle = useCallback(async (clientId) => {
    const response = await Conect.post(`/findvehicle/${getToken()}`, {
      plate: newOrder.plate.toUpperCase(), id_cli: newOrder.id_cli,
    });

    if (response?.data?.data?.id) return response?.data?.data?.id;
    const created = await Conect.post(`/createvehicle/${getToken()}`, {
      plate: newOrder.plate.toUpperCase(),
      year: newOrder.year,
      type: newOrder.vehicleType.toUpperCase(),
      id_cli: clientId,
      id_brand: Number(newOrder.brand),
      id_model: Number(newOrder.model),
      description: newOrder.vehicleDescription,
    });

    return created?.data.id;
  }, [newOrder]);

  // ─── Criar ordem ────────────────────────────────────────────────────────────

  const handleAddOrder = useCallback(async () => {
    if (!newOrder.cliente || !newOrder.plate) {
      NotificationsAlert(
        "warning",
        'Cliente e placa são obrigatórios',
        ""
      );
      return;
    }

    try {
      const clientId = newOrder.id_cli ?? await getOrCreateClient();
      const vehicleId = newOrder.id_vehicle ?? await getOrCreateVehicle(clientId);

      const response = await apiPost('/orders', {
        id_cli: clientId,
        id_vehicle: vehicleId,
        services: JSON.stringify(newOrder.serviceDescription),
      });

      if (!response) return;

      await fetchOrders();
      setModalOpen(false);
      resetOrder();
    } catch (err) {
      NotificationsAlert(
        "warning",
        'Erro ao cadastrar ordem!',
        err?.response?.data?.msg
      );
      //   return
    }
  }, [newOrder, getOrCreateClient, getOrCreateVehicle, fetchOrders, resetOrder]);


  // ─── Editar ordem ────────────────────────────────────────────────────────────
  const handleUpdateOrder = useCallback(async (updatedOrder) => {


    try {

      const token = getToken();

      const payload = {
        ...updatedOrder,

        services: JSON.stringify(
          updatedOrder.serviceDescription || []
        )
      };

      const response = await Conect.put(
        `/orders/${updatedOrder.id || updatedOrder.order_number}/${token}`,
        payload
      );

      if (!response.data.erro) {


        NotificationsAlert(
          "success",
          "",
          response.data.msg
        );

        fetchOrders()
      }

      if (response?.data?.erro) {
        console.error("Erro ao atualizar ordem:", response.data.msg);
        return;
      }

      // Atualiza lista local
      setOrders(prev =>
        prev.map(order =>
          (order.id || order.order_number) ===
            (updatedOrder.id || updatedOrder.order_number)
            ? {
              ...updatedOrder,
              services: updatedOrder.serviceDescription
            }
            : order
        )
      );

      // Atualiza ordem selecionada
      setSelectedOrder(updatedOrder);

      // Fecha modal
      setDetailsOpen(false);

    } catch (err) {
      NotificationsAlert(
        "danger",
        "",
        err?.response?.data?.msg || "Erro ao atualizar ordem."
      );
      console.error(
        "Erro ao atualizar ordem:",
        err?.response?.data || err
      );

    }
// eslint-disable-next-line
  }, []);

  const handleToggleServiceStatus = useCallback((order, serviceIndex) => {

    const updatedServices =
      order.serviceDescription.map((service, index) => {

        if (index === serviceIndex) {
          return {
            ...service,
            done: !service.done
          };
        }

        return service;
      });

    handleUpdateOrder({
      ...order,
      serviceDescription: updatedServices
    });

  }, [handleUpdateOrder]);

  // ─── Filtro com useMemo ──────────────────────────────────────────────────────

  const filteredOrders = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    if (!q) return orders;
    return orders.filter(o =>
      o.cliente?.toLowerCase().includes(q) ||
      o.plate?.toLowerCase().includes(q)
    );
  }, [orders, searchTerm]);

  // ─── Navegação ──────────────────────────────────────────────────────────────

  const handleVisualizarOrdem = useCallback((order) => {
    navigate('/print', { state: { order } });
  }, [navigate]);

  const handleOpenDetails = useCallback((order) => {
    setSelectedOrder(order);
    setDetailsOpen(true);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setDetailsOpen(false);
    setSelectedOrder(null);
  }, []);

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="o-orders-container">
      <OrderModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); resetOrder(); }}
        newOrder={newOrder}
        setNewOrder={setNewOrder}
        brands={brands}
        models={models}
        clientSuggestions={clientSuggestions}
        vehicleSuggestions={vehicleSuggestions}
        handleClientChange={handleClientChange}
        selectClient={selectClient}
        handlePlateChange={handlePlateChange}
        selectVehicle={selectVehicle}
        fetchModels={fetchModels}
        handleChange={handleChange}
        handleAddService={handleAddService}
        handleRemoveService={handleRemoveService}
        handleServiceChange={handleServiceChange}
        handleAddOrder={handleAddOrder}
      />
      {detailsOpen && (
        <OrderDetailsModal
          modalDetailsOpen={detailsOpen}
          setModalDetailsOpen={setDetailsOpen}
          order={selectedOrder}
          onClose={handleCloseDetails}
          onUpdate={handleUpdateOrder}
        />
      )}

      <div className="o-orders-header">
        <div className="o-orders-title-wrap">
          <h1 className="o-orders-title">Ordens de Serviço</h1>
          <span className="o-orders-count-badge">
            {filteredOrders.length} {filteredOrders.length === 1 ? 'ordem' : 'ordens'}
          </span>
        </div>

        <div className="o-orders-controls">
          <div className="o-search-wrap">
            <span className="o-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Buscar por cliente ou placa…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="o-add-btn" onClick={() => setModalOpen(true)}>
            + Nova Ordem
          </button>
        </div>
      </div>

      {loading ? (
        <div className="o-orders-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="o-order-card o-card-skeleton">
              <div className="o-skel-top" />
              <div className="o-skel-body">
                <div className="o-skel-line" />
                <div className="o-skel-line short" />
                <div className="o-skel-line" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="o-empty-state">
          {searchTerm
            ? `Nenhum resultado para "${searchTerm}"`
            : 'Nenhuma ordem cadastrada'}
        </div>
      ) : (
        <div className="o-orders-grid">
          {filteredOrders.map((order, index) => (
            <div key={order.order_number ?? index} className="o-order-card">

              <div className="o-card-top">
                <div>
                  <div className="o-card-id">
                    OS #{String(order.order_number ?? index + 1).padStart(4, '0')}
                    {order.date ? ` · ${order.date}` : ''}
                  </div>
                  <div className="o-card-client">{order.cliente}</div>
                  {order.projectName && (
                    <div className="o-card-fantasy">{order.projectName}</div>
                  )}
                </div>
                <StatusBadge status={order.status ?? 'open'} />
              </div>

              <div className="o-card-body">
                <div className="o-info-row">
                  <span className="o-info-label">Fone</span>
                  <span className="o-info-value">{order.phone || '—'}</span>
                  {order.whatsapp && (
                    <span className="o-whatsapp-icon"><IoLogoWhatsapp /></span>
                  )}
                </div>

                <hr className="o-card-divider" />

                <div className="o-info-row">
                  <span className="o-info-label">
                    {order.vehicleType === 'semi' ? 'Semi-Reb.' : 'Veículo'}
                  </span>
                  <span className="o-plate">{order.plate}</span>
                </div>

                <div className="o-info-row">
                  <span className="o-info-label">Modelo</span>
                  <span className="o-info-value">
                    {[order.brand, order.model, order.year].filter(Boolean).join(' · ')}
                  </span>
                </div>

                {order.vehicleDescription && (
                  <div className="o-info-row">
                    <span className="o-info-label">Desc.</span>
                    <span className="o-info-value">{order.vehicleDescription}</span>
                  </div>
                )}

                <>
                  {order.serviceDescription.map((serv, i) => (
                    <div
                      key={i}
                      className={`o-service-pill ${serv.done ? 'done' : ''}`}
                    >
                      <div className="o-service-left">

                        <button
                          type="button"
                          className={`o-service-check ${serv.done ? 'active' : ''}`}
                          onClick={() => handleToggleServiceStatus(order, i)}
                        >

                          <div className="o-service-check-circle">
                            {serv.done ? '✓' : ''}
                          </div>


                        </button>

                        <span className="o-service-name">
                          {serv.Service}
                        </span>

                      </div>

                      <span className="o-service-val">
                        R$ {String(serv.valor).replace('.', ',')}
                      </span>

                    </div>
                  ))}
                  <div className="o-total-row">
                    <span className="o-total-label">Total estimado</span>
                    <span className="o-total-val">
                      R$ {calcTotal(order.serviceDescription)}
                    </span>
                  </div>
                </>
              </div>

              <div className="o-card-footer">
                <button
                  className="o-action-btn"
                  onClick={() => handleVisualizarOrdem(order)}
                >
                  🖨️ Imprimir
                </button>
                <button
                  className="o-action-btn primary"
                  onClick={() => handleOpenDetails(order)}
                >
                  ✏️ Editar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Orders;